import { get, post, type RequestConfig } from "../shared/api-client";
import type {
  ApiItemResponse,
  ListQuery,
  PaginatedResult,
} from "@project/types/location";
import {
  parseGaushalaCreateOrderResponse,
  parseGaushalaVerifyPaymentResponse,
  type GaushalaCreateOrderPayload,
  type GaushalaCreateOrderResult,
  type GaushalaScheme,
  type GaushalaVerifyPaymentPayload,
  type GaushalaVerifyPaymentResult,
} from "@project/types/gaushala";
import {
  JIVANDHARA_BASE,
  SILENT_LIST,
  fetchListPage,
  isApiItemResponse,
  unwrapItem,
} from "../admin/list-api-helpers";

const SCHEMES_BASE = `${JIVANDHARA_BASE}/gaushala-schemes`;
const DONATIONS_BASE = `${JIVANDHARA_BASE}/gaushala-donations`;
const SILENT: RequestConfig = { showNotification: false };

class PublicGaushalaService {
  listActiveSchemes = (
    query: ListQuery = {}
  ): Promise<PaginatedResult<GaushalaScheme>> =>
    fetchListPage<GaushalaScheme>(`${SCHEMES_BASE}/`, {
      page_size: 100,
      ...query,
      status: "active",
    });

  getScheme = async (
    id: number | string
  ): Promise<GaushalaScheme | undefined> => {
    const data = await get<ApiItemResponse<GaushalaScheme> | GaushalaScheme>(
      `${SCHEMES_BASE}/${id}/`,
      SILENT_LIST
    );
    return unwrapItem(data);
  };

  createOrder = async (
    payload: GaushalaCreateOrderPayload
  ): Promise<GaushalaCreateOrderResult> => {
    const response = await post<unknown, GaushalaCreateOrderPayload>(
      `${DONATIONS_BASE}/create-order/`,
      payload,
      SILENT
    );
    const parsed = parseGaushalaCreateOrderResponse(
      unwrapItem(response) ?? response
    );
    if (!parsed.order_id) {
      throw new Error("Failed to create Gaushala payment order.");
    }
    return parsed;
  };

  verifyPayment = async (
    payload: GaushalaVerifyPaymentPayload
  ): Promise<GaushalaVerifyPaymentResult> => {
    const response = await post<unknown, GaushalaVerifyPaymentPayload>(
      `${DONATIONS_BASE}/verify-payment/`,
      payload,
      SILENT
    );
    if (isApiItemResponse(response)) {
      return parseGaushalaVerifyPaymentResponse(response.data ?? response);
    }
    return parseGaushalaVerifyPaymentResponse(unwrapItem(response) ?? response);
  };
}

export const publicGaushalaService = new PublicGaushalaService();
